import { animate, style, transition, trigger } from '@angular/animations';
import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { AccumulatedStatsType, DateRange, getPredefinedDateRangeList } from '@motes-sdk/models';

@Component({
    selector: 'time-range-picker',
    templateUrl: './time-range-picker.component.html',
    styleUrls: ['./time-range-picker.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [
        trigger('slideFromRight', [
            transition(':enter', [
                style({ transform: "translateX(100%)", opacity: 0, zIndex: 1000 }),
                animate(100, style({ transform: "translateX(0%)", opacity: 1, zIndex: 1001 }))
            ]),
            transition(':leave', [
                style({ opacity: 1 }),
                animate(60, style({ opacity: 0, transform: "translateX(100%)" }))
            ])
        ]),
    ]
})
export class TimeRangePickerComponent implements OnDestroy {
    // Get selected date object change events
    @Output() dateRangeChange = new EventEmitter<DateRange>();
    @Input('dateRange') set setDateRange(date: DateRange) { this.initTime(date); };
    @Input() initialPredefinedId: string = 'LAST_30_DAYS';
    @Input() darkMode: boolean = false;
    @Input() predefinedDatesIncluded: boolean = true;

    @ViewChild('predefinedRanges') predefinedRanges: ElementRef<HTMLDivElement>;

    public startDate: Date = null;
    public endDate: Date = null;
    public rangeLabel: string = "Last 90 days";
    public selectedRangeId: string = "LAST_90_DAYS";

    public PREDEFINED_DATE_RANGES: DateRange[] = getPredefinedDateRangeList();

    constructor(@Inject(DOCUMENT) private document: Document) {
    }

    ngOnInit() {
        // Init date to predefined range
        this.setPredefinedRange(this.PREDEFINED_DATE_RANGES.find( range => range.id == this.initialPredefinedId));
    }

    /**
     * Init local time range and emit it as output
     */
    private initTime(range: DateRange) {
        if(!range) return;
        this.startDate = new Date(range.startDate.getTime());
        this.endDate = new Date(range.endDate.getTime());
        this.dateRangeChange.emit(range);
    }

    /**
     * Called on date start selection via date popup
     */
    public startDateSelected(date: Date) {
        this.rangeLabel = "Custom";
        this.selectedRangeId = "CUSTOM";
        this.startDate = date;
    }

    /**
     * Called on date end selection via date popup
     */
    public endDateSelected(date: Date) {
        if(!date) return;
        // Find custom range in list
        let range = this.PREDEFINED_DATE_RANGES.find( range => range.id == 'CUSTOM');
        // Update view label and range
        this.rangeLabel = range.label;
        this.endDate = date;
        this.endDate.setDate(date.getDate() + 1);
        this.endDate.setMilliseconds(-1);
        // Calculate the number of days between start and end date
        const difference = this.endDate.getTime() - this.startDate.getTime();
        const dayInterval = Math.ceil(difference / (1000 * 3600 * 24));
        // Set to month interval in case a big time period is selected
        const type = (dayInterval > 365) ? AccumulatedStatsType.month : AccumulatedStatsType.day;

        this.dateRangeChange.emit({
            id: range.id,
            label: range.label,
            startDate: new Date(this.startDate),
            endDate: new Date(this.endDate),
            type: type
        });
    }

    /**
     * Select one of the predefined date ranges. Update local start and end date and emit range.
     */
    public setPredefinedRange(range: DateRange) {
        this.rangeLabel = range.label;
        this.startDate = range.startDate;
        this.endDate = range.endDate;
        this.selectedRangeId = range.id;

        this.dateRangeChange.emit({
            id: range.id,
            label: range.label,
            startDate: new Date(this.startDate),
            endDate: new Date(this.endDate),
            type: range.type
        });
    }

    private scrollListener: () => void;
    private animationFrameId: number;

    /**
     * Called when the date popup window is opened. Position the predefined ranges
     * relative to the actual date picker popup (which is rendered in a CDK overlay).
     */
    public datePopupOpened() {
        if(!this.predefinedDatesIncluded) return;

        // Wait for the popup to be rendered in the CDK overlay
        setTimeout(() => {
            this.updatePredefinedRangesPosition();
            
            // Add scroll listener to update position when scrolling
            this.scrollListener = () => {
                // Use requestAnimationFrame to ensure smooth positioning
                if (this.animationFrameId) {
                    cancelAnimationFrame(this.animationFrameId);
                }
                this.animationFrameId = requestAnimationFrame(() => {
                    this.updatePredefinedRangesPosition();
                });
            };
            window.addEventListener('scroll', this.scrollListener, true);
        }, 0);
    }

    private updatePredefinedRangesPosition() {
        const datePopup = this.document.querySelector<HTMLDivElement>('.mat-datepicker-popup');
        if (datePopup && this.predefinedRanges) {
            const popupRect = datePopup.getBoundingClientRect();
            const predefinedWidth = 180;
            
            // Position the predefined ranges to the left of the popup
            this.predefinedRanges.nativeElement.style.position = 'fixed';
            this.predefinedRanges.nativeElement.style.left = `${popupRect.left - predefinedWidth}px`;
            this.predefinedRanges.nativeElement.style.top = `${popupRect.top}px`;
            this.predefinedRanges.nativeElement.style.height = `${popupRect.height}px`;
            this.predefinedRanges.nativeElement.style.transform = 'none'; // Reset any transforms
        }
    }

    ngOnDestroy() {
        // Clean up scroll listener
        if (this.scrollListener) {
            window.removeEventListener('scroll', this.scrollListener, true);
        }
        // Clean up animation frame
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
        }
    }
}
